import React, { useState } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiSend,
  FiMessageSquare,
  FiUsers,
  FiGlobe,
  FiLock,
  FiInfo,
  FiAlertCircle,
  FiUserPlus,
  FiCheck,
} from "react-icons/fi";
import communityService from "../../services/communityService";
import "./styles/index.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", damping: 25, stiffness: 300 },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 },
  },
};

const JoinRequestModal = ({ isOpen, onClose, community, onSuccess }) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDirectJoin =
    community?.type === "public" && community?.settings?.allowDirectJoin;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!community) return;

    try {
      setLoading(true);
      setError(null);

      const isDirectJoin =
        community.type === "public" && community.settings?.allowDirectJoin;

      if (isDirectJoin) {
        await communityService.directJoinCommunity(community._id);
        toast.success("Successfully joined the community!");
      } else {
        await communityService.requestToJoinCommunity(
          community._id,
          message.trim()
        );
        toast.success("Join request sent successfully!");
      }

      if (onSuccess) {
        onSuccess();
      }

      onClose();
      setMessage("");
    } catch (error) {
const isDirectJoin =
        community.type === "public" && community.settings?.allowDirectJoin;
      setError(
        error.message ||
          `Failed to ${isDirectJoin ? "join community" : "send join request"}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="collab-comm-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleOverlayClick}
      >
        <motion.div
          className="collab-comm-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 480 }}
        >
          {/* Modal Header */}
          <div className="collab-comm-modal-header">
            <div className="collab-comm-modal-title">
              <div
                className="collab-comm-header-icon"
                style={{
                  width: 40,
                  height: 40,
                  background: isDirectJoin
                    ? "var(--collab-gradient-fresh)"
                    : "var(--collab-gradient-primary)",
                }}
              >
                <FiUserPlus size={20} />
              </div>
              <div>
                <h2>{isDirectJoin ? "Join Community" : "Request to Join"}</h2>
                <p
                  style={{
                    fontSize: "0.8125rem",
                    color: "var(--collab-slate-500)",
                    margin: 0,
                  }}
                >
                  {isDirectJoin ? "Join instantly" : "Submit a join request"}
                </p>
              </div>
            </div>
            <button className="collab-comm-modal-close" onClick={onClose}>
              <FiX size={20} />
            </button>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit}>
            <div className="collab-comm-modal-body">
              {/* Community Card */}
              <div
                style={{
                  background: "var(--collab-slate-50)",
                  borderRadius: "var(--collab-radius-lg)",
                  padding: "var(--collab-space-4)",
                  marginBottom: "var(--collab-space-5)",
                  border: "1px solid var(--collab-slate-200)",
                }}
              >
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "var(--collab-slate-800)",
                    margin: "0 0 var(--collab-space-2) 0",
                  }}
                >
                  {community?.name}
                </h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--collab-space-3)",
                    marginBottom: "var(--collab-space-3)",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color:
                        community?.type === "public"
                          ? "var(--collab-green)"
                          : "var(--collab-amber)",
                      background:
                        community?.type === "public"
                          ? "rgba(16, 185, 129, 0.1)"
                          : "rgba(245, 158, 11, 0.1)",
                      padding: "4px 8px",
                      borderRadius: "var(--collab-radius-full)",
                    }}
                  >
                    {community?.type === "public" ? (
                      <FiGlobe size={12} />
                    ) : (
                      <FiLock size={12} />
                    )}
                    {community?.type === "public" ? "Public" : "Private"}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "0.75rem",
                      color: "var(--collab-slate-500)",
                    }}
                  >
                    <FiUsers size={12} />
                    {community?.memberCount || 0} members
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "0.8125rem",
                    color: "var(--collab-slate-600)",
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {community?.description}
                </p>
              </div>

              {/* Info Section */}
              <div
                style={{
                  background: "var(--collab-yellow-soft)",
                  borderRadius: "var(--collab-radius-lg)",
                  padding: "var(--collab-space-4)",
                  marginBottom: "var(--collab-space-5)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--collab-space-2)",
                    marginBottom: "var(--collab-space-3)",
                    color: "var(--collab-amber-dark)",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  <FiInfo size={16} />
                  What happens next?
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "var(--collab-space-5)",
                    color: "var(--collab-slate-700)",
                    fontSize: "0.8125rem",
                    lineHeight: 1.6,
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--collab-space-2)",
                  }}
                >
                  {isDirectJoin ? (
                    <>
                      <li>You'll join the community instantly</li>
                      <li>Immediate access to discussions and features</li>
                      <li>Start participating in chats right away</li>
                    </>
                  ) : (
                    <>
                      <li>Your request will be reviewed by moderators</li>
                      <li>You'll receive a notification when processed</li>
                      <li>If approved, you'll gain full community access</li>
                    </>
                  )}
                </ul>
              </div>

              {/* Message Input (only for non-direct join) */}
              {!isDirectJoin && (
                <div className="collab-comm-form-group">
                  <label
                    className="collab-comm-form-label"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--collab-space-2)",
                    }}
                  >
                    <FiMessageSquare size={14} />
                    Message to Moderators (Optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell the moderators why you'd like to join..."
                    maxLength={200}
                    rows={3}
                    className="collab-comm-form-textarea"
                  />
                  <div className="collab-comm-form-hint">
                    {message.length}/200 characters
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div
                  className="collab-comm-form-error"
                  style={{ marginBottom: "var(--collab-space-4)" }}
                >
                  <FiAlertCircle size={14} />
                  {error}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="collab-comm-modal-footer">
              <button
                type="button"
                className="collab-comm-btn collab-comm-btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="collab-comm-btn collab-comm-btn-primary"
                disabled={loading}
                style={{
                  background: isDirectJoin
                    ? "var(--collab-gradient-fresh)"
                    : "var(--collab-gradient-primary)",
                }}
              >
                {loading ? (
                  <>
                    <div
                      className="collab-comm-spinner"
                      style={{ width: 16, height: 16, borderWidth: 2 }}
                    />
                    {isDirectJoin ? "Joining..." : "Sending..."}
                  </>
                ) : (
                  <>
                    {isDirectJoin ? (
                      <FiCheck size={16} />
                    ) : (
                      <FiSend size={16} />
                    )}
                    {isDirectJoin ? "Join Now" : "Send Request"}
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JoinRequestModal;
